import { injectPosthogSnippet } from "./posthog_snippet";
import { whenConsentGranted } from "./cookiebot_consent";

const setupPostHog = () => {
	const configEl = document.getElementById("posthog-config");
	if (!configEl) return;

	let config;
	try {
		config = JSON.parse(configEl.textContent);
	} catch (e) {
		console.error("Failed to parse PostHog config", e);
		return;
	}

	const {
		enabled,
		debug,
		api_key,
		api_host,
		ui_host,
		cookie_domain,
		degraded_cooldown_seconds,
		bootstrap_cache_ttl_seconds,
		load_timeout_ms,
		anon_distinct_id,
		logged_in_distinct_id,
		backend_bootstrap_flags,
		active_sub_count,
		framer_variant,
	} = config;

	const POSTHOG_DEGRADED_KEY = "posthog:degraded_until";
  const cacheId = logged_in_distinct_id || anon_distinct_id || "anon";
	const POSTHOG_BOOTSTRAP_CACHE_KEY = "posthog:bootstrap_flags";
	const POSTHOG_EXPOSURES_KEY = "posthog:experiment_exposures";
	const nowMs = () => Date.now();

	const safeStorage = {
		get(key) {
			try {
				return sessionStorage.getItem(key);
			} catch (_e) {
				return null;
			}
		},
		set(key, value) {
			try {
				sessionStorage.setItem(key, value);
				return true;
			} catch (_e) {
				return false;
			}
		},
		remove(key) {
			try {
				sessionStorage.removeItem(key);
				return true;
			} catch (_e) {
				return false;
			}
		},
	};

	const markPosthogDegraded = () => {
		const degradedUntilMs = nowMs() + degraded_cooldown_seconds * 1000;
		safeStorage.set(POSTHOG_DEGRADED_KEY, String(degradedUntilMs));
	};

	const isPosthogDegraded = () => {
		const degradedUntilMs = Number(
			safeStorage.get(POSTHOG_DEGRADED_KEY) || "0",
		);
		return degradedUntilMs > nowMs();
	};

	/**
	 * Returns cached flags only when they belong to the current identity, so flags
	 * cached while anonymous are never served after login in the same tab.
	 * @returns {Object|null}
	 */
	const readCachedBootstrapFlags = () => {
		try {
			const raw = safeStorage.get(POSTHOG_BOOTSTRAP_CACHE_KEY);
			if (!raw) return null;
			const { id, ts, flags } = JSON.parse(raw) || {};
			if (id !== cacheId) return null;

			const cachedAtMs = Number(ts) || 0;
			const isFresh =
				cachedAtMs > 0 &&
				cachedAtMs + bootstrap_cache_ttl_seconds * 1000 > nowMs();
			if (!isFresh) return null;

			return flags && typeof flags === "object" ? flags : null;
		} catch (_e) {
			return null;
		}
	};

	const writeCachedBootstrapFlags = (flags) => {
		if (!flags || typeof flags !== "object") return;
		if (Object.keys(flags).length === 0) return;

		safeStorage.set(
			POSTHOG_BOOTSTRAP_CACHE_KEY,
			JSON.stringify({ id: cacheId, ts: nowMs(), flags }),
		);
	};

	const clearCachedBootstrapFlags = () => {
		safeStorage.remove(POSTHOG_BOOTSTRAP_CACHE_KEY);
	};

	const livePosthog = () => window.posthog;

	window.safePosthogCapture = (eventName, properties = {}) => {
		const ph = livePosthog();
		if (!ph || typeof ph.capture !== "function") return false;
		try {
			ph.capture(eventName, properties);
			return true;
		} catch (_e) {
			return false;
		}
	};

	window.safePosthogIdentify = (distinctId) => {
		const ph = livePosthog();
		if (!ph || typeof ph.identify !== "function") return false;
		try {
			ph.identify(distinctId, { email: distinctId });
			return true;
		} catch (_e) {
			return false;
		}
	};

  const readExperimentFlags = () => {
    try {
      const raw = document.body?.dataset?.posthogExperimentFlags;
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((registration) => {
          if (typeof registration === "string") {
            return { key: registration, flagType: "multivariate" };
          }

          if (!registration || typeof registration.key !== "string") return null;
          return {
            key: registration.key,
            flagType: registration.flag_type === "boolean" ? "boolean" : "multivariate",
          };
        })
        .filter(Boolean);
    } catch (_e) {
      return [];
    }
  };

  const experimentFlags = readExperimentFlags();
  const reportedExposures = new Set();
  const exposureId = anon_distinct_id || "anon";

  /**
   * Flag keys already reported for the current anon ID in this tab. A stored list
   * for a different ID counts as empty.
   * @returns {string[]}
   */
  const readStoredExposures = () => {
    try {
      const { id, keys } = JSON.parse(safeStorage.get(POSTHOG_EXPOSURES_KEY)) || {};
      return id === exposureId && Array.isArray(keys) ? keys : [];
    } catch (_e) {
      return [];
    }
  };

  const captureFeatureFlagExposure = (flagKey, flagValue) => {
    if (!flagKey || reportedExposures.has(flagKey)) return;
    const storedExposures = readStoredExposures();
    if (storedExposures.includes(flagKey)) return;

    try {
      const success = window.safePosthogCapture("$feature_flag_called", {
        $feature_flag: flagKey,
        $feature_flag_response: flagValue,
        $feature_flag_source: "page_bootstrap",
      });

      if (success) {
        reportedExposures.add(flagKey);
        safeStorage.set(
          POSTHOG_EXPOSURES_KEY,
          JSON.stringify({ id: exposureId, keys: [...storedExposures, flagKey] }),
        );
      }
    } catch (_e) {}
  };

  const fireExperimentExposureEvents = () => {
    experimentFlags.forEach(({ key: flagKey, flagType }) => {
      try {
        const hasBootstrapValue = Object.prototype.hasOwnProperty.call(
          window.posthogFeatureFlagsSnapshot,
          flagKey,
        );
        if (!hasBootstrapValue) return;

        const flagValue = window.posthogFeatureFlagsSnapshot[flagKey];
        // Multivariate false means the visitor is outside the rollout cohort. For boolean
        // flags, false is the evaluated off arm and must be included in exposure reporting.
        if (flagValue == null || (flagValue === false && flagType !== "boolean")) return;

        captureFeatureFlagExposure(flagKey, flagValue);
      } catch (_e) {}
    });
  };

	const cachedFlags = readCachedBootstrapFlags() || {};
	const hasBackendBootstrapFlags =
		backend_bootstrap_flags && typeof backend_bootstrap_flags === "object";
	const selectedBootstrapFlags = hasBackendBootstrapFlags
		? backend_bootstrap_flags
		: cachedFlags;
	window.posthogFeatureFlagsSnapshot = selectedBootstrapFlags;

	if (hasBackendBootstrapFlags) {
		if (Object.keys(backend_bootstrap_flags).length === 0) {
			clearCachedBootstrapFlags();
		} else {
			writeCachedBootstrapFlags(backend_bootstrap_flags);
		}
	}

	if (!enabled || isPosthogDegraded()) return;

	const initPosthog = () => {
		try {
			let initTimedOut = false;
			const initTimeoutId = window.setTimeout(() => {
				initTimedOut = true;
				markPosthogDegraded();
			}, load_timeout_ms);

			injectPosthogSnippet(api_host);

			posthog.init(api_key, {
				api_host: api_host,
				ui_host: ui_host,
				debug: debug,
				person_profiles: "always",
				disable_session_recording: window.location.pathname.startsWith("/admin"),
				autocapture: !window.location.pathname.startsWith("/admin"),
				// posthog-js defaults this to true, which silently blocks toolbar-built experiments.
				disable_web_experiments: false,
				before_send: window.posthogCustomizations
					? window.posthogCustomizations.sampleByEvent(["$web_vitals"], 0.3)
					: undefined,
				cookie_domain: cookie_domain || null,
				session_recording: {
					maskAllInputs: true,
					maskInputFn: (text, element) => {
						if (element?.dataset.capture === "true") {
							return text;
						}
						return "*".repeat(text.trim().length);
					},
				},
				bootstrap: {
					distinctID: anon_distinct_id,
					isIdentifiedID: false,
					featureFlags: selectedBootstrapFlags,
				},
				// Runs after the real SDK replaces the snippet stub; sync state queries are reliable here.
				loaded: (ph) => {
					try {
						const pinnedDeviceId = ph.get_property("$device_id");
						if (typeof pinnedDeviceId === "string" && pinnedDeviceId.includes("@")) {
							ph.reset(true);
						}
					} catch (_e) {}

					if (logged_in_distinct_id) {
						try {
							const currentDistinctId = ph.get_distinct_id();
							const userState = ph.get_property("$user_state");
							if (currentDistinctId !== logged_in_distinct_id || userState === "anonymous") {
								ph.identify(logged_in_distinct_id, {
									email: logged_in_distinct_id,
								});
							}
						} catch (_e) {}
					}

	        // The injected snippet queues capture calls before the real SDK is ready.
	        // Capture only after replacement so the session receipt does not suppress a retry.
	        fireExperimentExposureEvents();
				},
			});

			if (!initTimedOut) {
				window.clearTimeout(initTimeoutId);
			}
		} catch (_e) {
			markPosthogDegraded();
			return;
		}

		posthog.register({ active_sub_count: active_sub_count });
		if (framer_variant) {
			posthog.register_once({ framer_variant: framer_variant });
		}

		// Keep a lightweight cookie with the PostHog session ID so
		// server-side events can include `$session_id` and link to replays.
		const syncPhSessionCookie = () => {
			const write = (sid) => {
				if (!sid) return;
				document.cookie =
					"ph_session_id=" +
					sid +
					"; Path=/" +
					"; SameSite=Lax" +
					"; Max-Age=86400" +
					(cookie_domain ? "; Domain=" + cookie_domain : "") +
					(location.protocol === "https:" ? "; Secure" : "");
			};

			const ph = window.posthog;
			if (ph?.get_session_id) write(ph.get_session_id());
			if (ph?.onSessionId) ph.onSessionId(write);
		};

		syncPhSessionCookie();
	};

	// A statistics opt-out stops capture and replay. The gate sits below the flag
	// snapshot on purpose: server-evaluated flags still drive product behavior for
	// opted-out visitors. Consent can also land after DOMContentLoaded, so wait for
	// it rather than drop analytics for the rest of the page.
	whenConsentGranted("statistics", initPosthog);

};

document.addEventListener("DOMContentLoaded", setupPostHog);
